import React from 'react';
import clsx from 'clsx';
import { ListBulletIcon, ViewColumnsIcon } from '@heroicons/react/16/solid';

export type ListViewMode = 'list' | 'kanban';

/**
 * List / board, as a small segmented control that lives in the view's own
 * toolbar — the filter bar in the list, the matching bar over the board —
 * rather than in a page header. Same place in both views, so switching
 * never moves it out from under the pointer.
 */
export const ViewSwitch: React.FC<{
  value: ListViewMode;
  onChange: (next: ListViewMode) => void;
}> = ({ value, onChange }) => {
  const option = (
    mode: ListViewMode,
    label: string,
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  ) => (
    <button
      type="button"
      onClick={() => onChange(mode)}
      aria-pressed={value === mode}
      aria-label={label}
      title={label}
      className={clsx(
        'rounded p-1 transition-colors',
        value === mode ? 'bg-fg/10 text-fg' : 'text-fg-muted hover:text-fg'
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
    </button>
  );

  return (
    <div role="group" aria-label="View" className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
      {option('list', 'List view', ListBulletIcon)}
      {option('kanban', 'Board view', ViewColumnsIcon)}
    </div>
  );
};

export default ViewSwitch;
